<template>
    <div class="blog-list-container">
        <div class="header-section mb-16 text-center">
            <h1 class="text-5xl font-bold text-white mb-6 tracking-tight">
                Latest Posts
            </h1>
            <div class="w-32 h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 mx-auto rounded-full">
            </div>
        </div>

        <!-- Loading state -->
        <div v-if="isLoading" class="text-center py-16">
            <div class="spinner"></div>
            <p class="mt-6 text-gray-300 text-lg">Loading amazing content...</p>
        </div>

        <!-- Error message -->
        <div v-else-if="error" class="text-center py-16">
            <div class="bg-red-500/10 p-8 rounded-2xl border border-red-500/20 max-w-2xl mx-auto">
                <p class="text-red-400 text-lg">{{ error }}</p>
            </div>
        </div>

        <!-- Blog list -->
        <div v-else-if="publishedBlogs.length > 0">
            <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                <div v-for="blog in paginatedBlogs" :key="blog.id" class="blog-card group">
                    <!-- Featured Image -->
                    <div v-if="blog.featuredImage" class="featured-image-container">
                        <img :src="blog.featuredImage" :alt="blog.title" class="featured-image">
                    </div>
                    <div v-else class="featured-image-container default-image">
                        <div class="default-image-content">
                            <span class="text-4xl mb-2">✍️</span>
                            <span class="text-sm opacity-60">Blog Post</span>
                        </div>
                    </div>
                    <div class="content-wrapper">
                        <!-- Meta Info -->
                        <div class="meta-info">
                            <time :datetime="blog.createdAt">{{ formatDate(blog.createdAt) }}</time>
                        </div>

                        <!-- Title -->
                        <h2 class="title">
                            <router-link :to="{ name: 'Blog', params: { name: blog.name } }">
                                {{ blog.title || blog.name }}
                            </router-link>
                        </h2>

                        <!-- Description -->
                        <p v-if="blog.description" class="description">
                            {{ blog.description }}
                        </p>

                        <!-- Tags -->
                        <div v-if="blog.tags && blog.tags.length > 0" class="tags-container">
                            <span v-for="tag in blog.tags" :key="tag" class="tag">
                                {{ tag }}
                            </span>
                        </div>

                        <router-link :to="{ name: 'Blog', params: { name: blog.name } }" class="read-more-link">
                            Read Article
                            <span class="arrow">→</span>
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="pagination-container">
                <button @click="currentPage > 1 && (currentPage--)" :disabled="currentPage === 1"
                    class="pagination-button prev" :class="{ 'disabled': currentPage === 1 }">
                    <span class="arrow">←</span>
                    Previous
                </button>

                <div class="pages-container">
                    <button v-for="page in displayedPages" :key="page" @click="currentPage = page" class="page-number"
                        :class="{ 'active': currentPage === page }">
                        {{ page }}
                    </button>
                </div>

                <button @click="currentPage < totalPages && (currentPage++)" :disabled="currentPage === totalPages"
                    class="pagination-button next" :class="{ 'disabled': currentPage === totalPages }">
                    Next
                    <span class="arrow">→</span>
                </button>
            </div>
        </div>

        <!-- No data message -->
        <div v-if="!isLoading && !error && publishedBlogs.length === 0" class="empty-state">
            <div class="empty-state-content">
                <span class="empty-icon">📝</span>
                <h3 class="empty-title">No Posts Yet</h3>
                <p class="empty-description">Stay tuned for amazing content coming soon!</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, inject, computed } from 'vue'

// Inject API base URL
const apiBaseUrl = inject('apiBaseUrl')

const blogs = ref([])
const isLoading = ref(true)
const error = ref(null)
const currentPage = ref(1)
const itemsPerPage = 10

// Format date
const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return ''

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).format(date)
    } catch (error) {
        console.error('Date formatting error:', error)
        return ''
    }
}

// Filter published blogs
const publishedBlogs = computed(() => {
    return blogs.value.filter(blog => blog.status === 'published')
})

// Computed properties for pagination
const totalPages = computed(() => Math.ceil(publishedBlogs.value.length / itemsPerPage))

const paginatedBlogs = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage
    const end = start + itemsPerPage
    return publishedBlogs.value.slice(start, end)
})

const displayedPages = computed(() => {
    const delta = 2
    const range = []
    const rangeWithDots = []
    let l

    for (let i = 1; i <= totalPages.value; i++) {
        if (i === 1 || i === totalPages.value ||
            (i >= currentPage.value - delta && i <= currentPage.value + delta)) {
            range.push(i)
        }
    }

    range.forEach(i => {
        if (l) {
            if (i - l === 2) {
                rangeWithDots.push(l + 1)
            } else if (i - l !== 1) {
                rangeWithDots.push('...')
            }
        }
        rangeWithDots.push(i)
        l = i
    })

    return rangeWithDots
})

// Load blog list data
const loadBlogs = async () => {
    isLoading.value = true
    error.value = null

    try {
        const response = await fetch(apiBaseUrl + '/API/readJson.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({ jsonUrl: 'blogs.json' })
        })

        const data = await response.json()
        if (!response.ok) {
            throw new Error(data?.message || `HTTP ${response.status}`)
        }
        if (data && data.blogs) {
            blogs.value = data.blogs
            // 如果当前没有已发布的博客，显示提示信息
            if (publishedBlogs.value.length === 0) {
                error.value = 'No published blog posts available'
            }
        } else {
            error.value = 'Unable to load blog data'
        }
    } catch (err) {
        console.error('Failed to load blog list:', err)
        error.value = 'Error loading blog list'
    } finally {
        isLoading.value = false
    }
}

onMounted(() => {
    loadBlogs()
})
</script>

<style scoped>
.blog-list-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 3rem 2rem;
    min-height: 100vh;
}

.blog-card {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.4s ease;
    border: 1px solid rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: stretch;
}

.blog-card:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
}

.featured-image-container {
    width: 140px;
    min-width: 140px;
    height: auto;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 16px 0 0 16px;
    position: relative;
    display: flex;
    align-items: center;
}

.default-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1));
}

.default-image-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: rgba(255, 255, 255, 0.7);
}

.featured-image {
    width: 140px;
    height: max-content;
    /* object-fit: cover;
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
    min-height: 140px; */
}

.blog-card:hover .featured-image {
    transform: scale(1.08);
}

.featured-image-container::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.2) 100%);
    pointer-events: none;
    transition: opacity 0.3s ease;
}

.blog-card:hover .featured-image-container::after {
    opacity: 0.7;
}

.content-wrapper {
    padding: 1.25rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.meta-info {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 1rem;
}

.title {
    font-size: 1.25rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 0.5rem;
    line-height: 1.3;
}

.title a {
    text-decoration: none;
    color: inherit;
    transition: color 0.3s ease;
}

.title a:hover {
    color: #60A5FA;
}

.description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.tag {
    padding: 0.25rem 0.75rem;
    background: rgba(59, 130, 246, 0.1);
    color: #60A5FA;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    transition: all 0.3s ease;
}

.tag:hover {
    background: rgba(59, 130, 246, 0.2);
}

.read-more-link {
    display: inline-flex;
    align-items: center;
    color: #60A5FA;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.3s ease;
}

.arrow {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
}

.read-more-link:hover .arrow {
    transform: translateX(4px);
}

.pagination-container {
    margin-top: 3rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
}

.pagination-button {
    display: inline-flex;
    align-items: center;
    padding: 0.75rem 1.25rem;
    background: rgba(59, 130, 246, 0.1);
    color: white;
    border-radius: 12px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.pagination-button:not(.disabled):hover {
    background: rgba(59, 130, 246, 0.2);
}

.pagination-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-button.prev .arrow {
    margin-right: 0.5rem;
    margin-left: 0;
}

.pages-container {
    display: flex;
    gap: 0.5rem;
}

.page-number {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: rgba(59, 130, 246, 0.1);
    color: white;
    transition: all 0.3s ease;
}

.page-number:hover:not(.active) {
    background: rgba(59, 130, 246, 0.2);
}

.page-number.active {
    background: #60A5FA;
    color: white;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(59, 130, 246, 0.1);
    border-radius: 50%;
    border-top-color: #60A5FA;
    animation: spin 1s linear infinite;
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
}

.empty-state-content {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 2rem;
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0;
}

.empty-title {
    font-size: 1.5rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 0.5rem;
}

.empty-description {
    color: rgba(255, 255, 255, 0.6);
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .blog-card {
        flex-direction: row;
    }

    .featured-image-container {
        width: 120px;
        min-width: 120px;
    }


    .content-wrapper {
        padding: 1rem;
    }

    .title {
        font-size: 1.25rem;
    }

    .pagination-container {
        flex-wrap: wrap;
    }
}
</style>
